import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";

import { REACT_APP_AUTH0_CLAIMS_NAMESPACE } from "../configs/config";

/** Auth0 custom claim key for roles, derived from the env-specific namespace. */
const AUTH0_ROLES_CLAIM = `${REACT_APP_AUTH0_CLAIMS_NAMESPACE}roles`;

/** Admin role identifier. */
const ADMIN_ROLE = "admin";

/**
 * Hook that checks whether the current user has the admin role.
 * Reads Auth0 ID token claims for the custom roles namespace claim.
 * Falls back to false if claims are unavailable.
 * @returns Object with `isAdmin` boolean and `isLoading` boolean
 */
const useIsAdmin = (): { isAdmin: boolean; isLoading: boolean } => {
  const { getIdTokenClaims } = useAuth0();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const claims = await getIdTokenClaims();
        const roles: string[] = (claims?.[AUTH0_ROLES_CLAIM] as string[]) ?? [];
        setIsAdmin(roles.includes(ADMIN_ROLE));
      } catch {
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkAdmin();
  }, [getIdTokenClaims]);

  return { isAdmin: isAdmin, isLoading: isLoading };
};

export { useIsAdmin };
